import React, { useRef } from "react";
import { Navigate, useOutletContext } from "react-router-dom";

import { W0, W1 } from "@components/Constants";
import { hasAccess } from "@components/Authorization";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import OrganizationPublicKeysTable from "@components/OrganizationPublicKeys";
import { OrganizationIntegration } from "@modules/backstage/organizations";
import {
  OrganizationUpdateModal,
  isExternalInvestorPortalEnabled,
} from "@components/Organization";

const LABEL_UPDATE_LINK_TEMPLATES_TITLE = "Edit Custom Links";
const PATH_SETTINGS_ORGANIZATION = "/backstage/settings/organization";
const DESCRIPTION_LINK_TEMPLATES = "Your investors get notification emails with links back to a portal. Give the URL of each page of your own portal below. The emails then link to your portal, and not to the Fundamental one.";

const PERMISSION_READ_PUBLIC_KEYS = "organization-public-keys-read";

type LinkTemplates = {
  documentsUrl?: string;
  investmentSignThankYouUrl?: string;
  investmentSignUrl?: string;
  investmentUrl?: string;
  portalUrl?: string;
  signInUrl?: string;
  softCommitmentUrl?: string;
};

type Organization = {
  linkTemplates?: LinkTemplates;
};

type OutletContext = {
  organization: Organization;
};

type ModalRef = {
  openItem: (organization: Organization) => void;
};

const SettingsIntegrationPage = (): JSX.Element => {
  const updateLinkTemplatesModal = useRef<ModalRef>({} as ModalRef);

  const { organization } = useOutletContext<OutletContext>();
  const { getOrganizationId } = useAppContext() as AppContext;

  const shouldRedirect = !isExternalInvestorPortalEnabled(getOrganizationId());

  if (shouldRedirect) {
    return <Navigate to={PATH_SETTINGS_ORGANIZATION} replace />;
  }

  const style = {
    padding: `${W1}px ${W1 + W0}px`,
  };
  // NOTE: the custom links description list stays narrow, the public keys
  //       table needs the full width of the settings pane for its columns.
  const linkTemplatesStyle = {
    maxWidth: 650,
  };

  const openUpdateLinkTemplates = () =>
    updateLinkTemplatesModal.current.openItem(organization);

  const canReadPublicKeys = hasAccess([ PERMISSION_READ_PUBLIC_KEYS ]);

  return (
    <div style={style}>
      <div style={linkTemplatesStyle}>
        <OrganizationIntegration
          organization={organization}
          openUpdateLinkTemplates={openUpdateLinkTemplates}
          description={DESCRIPTION_LINK_TEMPLATES}
        />
      </div>

      {canReadPublicKeys && (
        <OrganizationPublicKeysTable />
      )}

      <OrganizationUpdateModal
        key="updateLinkTemplates"
        modal={updateLinkTemplatesModal}
        title={LABEL_UPDATE_LINK_TEMPLATES_TITLE}
        organization={organization}
        onlyFieldNames={[
          "linkTemplates",
        ]}
      />
    </div>
  );
};

export default SettingsIntegrationPage;
